import { Control, ControllerRenderProps, FieldPath } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { US_STATES } from "@/lib/usStates";

type AddressFieldName =
  | "address1"
  | "address2"
  | "city"
  | "state"
  | "zipCode"
  | "county"
  | "country";

type AddressFieldsProps<TFormValues extends Record<string, unknown>> = {
  control: Control<TFormValues>;
  disabled?: boolean;
  fieldNames?: Partial<Record<AddressFieldName, FieldPath<TFormValues>>>;
};

type RenderProps<TFormValues extends Record<string, unknown>> = {
  field: ControllerRenderProps<TFormValues>;
};

const DEFAULT_FIELD_NAMES: Record<AddressFieldName, string> = {
  address1: "address1",
  address2: "address2",
  city: "city",
  state: "state",
  zipCode: "zipCode",
  county: "county",
  country: "country",
};

const stateSelectItems = US_STATES.map((state) => (
  <SelectItem key={state.code} value={state.code}>
    {state.name} ({state.code})
  </SelectItem>
));

function resolveFieldNames<TFormValues extends Record<string, unknown>>(
  overrides?: Partial<Record<AddressFieldName, FieldPath<TFormValues>>>
) {
  return (Object.keys(DEFAULT_FIELD_NAMES) as AddressFieldName[]).reduce(
    (accumulator, key) => {
      const override = overrides?.[key];
      const fallback = DEFAULT_FIELD_NAMES[key];
      accumulator[key] = (override ?? (fallback as FieldPath<TFormValues>));
      return accumulator;
    },
    {} as Record<AddressFieldName, FieldPath<TFormValues>>
  );
}

export function AddressFields<TFormValues extends Record<string, unknown>>({
  control,
  disabled = false,
  fieldNames,
}: AddressFieldsProps<TFormValues>) {
  const resolvedFieldNames = resolveFieldNames(fieldNames);

  const renderTextField = (
    label: string,
    placeholder: string,
    field: ControllerRenderProps<TFormValues>
  ) => {
    const safeValue =
      typeof field.value === "string" || typeof field.value === "number"
        ? String(field.value)
        : "";

    return (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <FormControl>
          <Input
            placeholder={placeholder}
            {...field}
            disabled={disabled}
            value={safeValue}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    );
  };

  const renderAddress1Field = ({ field }: RenderProps<TFormValues>) => {
    return renderTextField("Address 1", "Street address", field);
  };

  const renderAddress2Field = ({ field }: RenderProps<TFormValues>) => {
    return renderTextField("Address 2", "Apt, Suite, etc.", field);
  };

  const renderCityField = ({ field }: RenderProps<TFormValues>) => {
    return renderTextField("City", "City", field);
  };

  const renderStateField = ({ field }: RenderProps<TFormValues>) => {
    const currentValue =
      typeof field.value === "string" && field.value.length > 0
        ? field.value
        : undefined;

    return (
      <FormItem>
        <FormLabel>State</FormLabel>
        <Select
          disabled={disabled}
          value={currentValue}
          onValueChange={field.onChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select state" />
          </SelectTrigger>
          <SelectContent>{stateSelectItems}</SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    );
  };

  const renderZipField = ({ field }: RenderProps<TFormValues>) => {
    return renderTextField("Zip", "Zip", field);
  };

  const renderCountyField = ({ field }: RenderProps<TFormValues>) => {
    return renderTextField("County", "County", field);
  };

  const renderCountryField = ({ field }: RenderProps<TFormValues>) => {
    return renderTextField("Country", "Country", field);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={control}
          name={resolvedFieldNames.address1}
          render={renderAddress1Field}
        />
        <FormField
          control={control}
          name={resolvedFieldNames.address2}
          render={renderAddress2Field}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField
          control={control}
          name={resolvedFieldNames.city}
          render={renderCityField}
        />
        <FormField
          control={control}
          name={resolvedFieldNames.state}
          render={renderStateField}
        />
        <FormField
          control={control}
          name={resolvedFieldNames.zipCode}
          render={renderZipField}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={control}
          name={resolvedFieldNames.county}
          render={renderCountyField}
        />
        <FormField
          control={control}
          name={resolvedFieldNames.country}
          render={renderCountryField}
        />
      </div>
    </div>
  );
}

